import { useAI } from '../hooks/useAI';
import { useState } from 'react';
import {
  CoercedQuestionType,
  coerceUnknownQuestionTypes,
  extractValidJson,
  FormTemplate,
} from '@employ-engage/data';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormPreview } from '../form-preview/form-preview';
import InlineLoadingSpinner from '../inline-loading-spinner/inline-loading-spinner';
import { useAuthUser } from '../contexts/auth-context-type';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { CREATE_FORM_TEMPLATE } from '../api';

export interface AiPromptChatProps {
  promptForUser: string;
  promptForAI: string;
  setOpen: (open: boolean) => void;
  onSave?: (surveyTemplateId: string) => void;
}

export const AiPromptChat = ({
  promptForUser,
  promptForAI,
  setOpen,
  onSave,
}: AiPromptChatProps) => {
  const { t } = useTranslation(['common']);
  const { loading, getAiResponse } = useAI();
  const [response, setResponse] = useState<any | null>(null);
  const [error, setError] = useState<any | null>(null);
  const [coerced, setCoerced] = useState<CoercedQuestionType[]>([]);
  const { user } = useAuthUser();
  const [createFormTemplate] = useMutation(CREATE_FORM_TEMPLATE);

  const { handleSubmit, values, handleChange, resetForm } = useFormik({
    initialValues: {
      prompt: '',
    },
    validationSchema: Yup.object().shape({
      prompt: Yup.string().required(t('Required')),
    }),
    onSubmit: async ({ prompt }) => {
      try {
        setError(null);
        const surveyObject = await getAiResponse(promptForAI + prompt);
        setResponse(surveyObject);
        resetForm();
      } catch (error: any) {
        if (error.code === 'response-error' && error.name === 'Error') {
          const safetyRatings =
            error.customErrorData?.response?.candidates[0]?.safetyRatings;
          if (safetyRatings) {
            const explicitContentRating = safetyRatings.find(
              (rating: any) =>
                rating.category === 'HARM_CATEGORY_SEXUALLY_EXPLICIT'
            );
            if (
              explicitContentRating &&
              explicitContentRating.probability !== 'NEGLIGIBLE'
            ) {
              console.error('Explicit content detected:', error);
              setError(
                t(
                  'This content may be inappropriate, please ask a different question!'
                )
              );
              return;
            }
          }
        }
        console.error('Error getting AI response:', error);
        setError(error);
      }
    },
  });

  return (
    <div className="w-full h-full relative min-h-[80vh] mt-5 overflow-y-auto">
      <div className="h-full pb-20">
        {!!coerced.length && (
          <p className="rounded bg-semantic-warning/20 p-3 text-sm">
            {t(
              '{{count}} questions asked for a type this app does not have and ' +
                'were changed to short text: {{list}}. Edit the template to ' +
                'change them.',
              {
                count: coerced.length,
                list: coerced
                  .map((c) => `"${c.question}" (${c.from})`)
                  .join(', '),
              }
            )}
          </p>
        )}
        {!!response && (
          <>
            <FormPreview form={extractValidJson(response) as FormTemplate} />
            <hr />
            <div className="flex justify-center">
              <button
                className="mt-20 bg-dark-primaryBackground text-white px-4 py-2 rounded-md"
                onClick={async () => {
                  try {
                    // A model will invent a question type — "rating",
                    // "multiple_choice" — and one that reaches the database
                    // renders as nothing and blocks submit for every
                    // recipient. Coerced to short text so the question
                    // survives, and reported so nobody finds out later.
                    const { template, coerced } = coerceUnknownQuestionTypes(
                      extractValidJson(response)
                    );
                    setCoerced(coerced);

                    const newResponse = await createFormTemplate({
                      variables: {
                        input: {
                          ...(template as object),
                          ownerId: user?.id,
                        },
                      },
                    });

                    onSave?.(newResponse.data.createFormTemplate.id);
                    setOpen(false);
                  } catch (error) {
                    console.error('Error saving template:', error);
                  }
                }}
              >
                {t('Use this template')}
              </button>
            </div>
          </>
        )}
        <div className="w-full grid justify-items-center mt-3 gap-3">
          {!response && !error && <div>{promptForUser}</div>}
          {loading && <InlineLoadingSpinner />}
          {error && (
            <span className="text-semantic-error">{JSON.stringify(error)}</span>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="absolute bottom-0 left-0 right-0 w-full flex items-center"
        >
          <input
            id="prompt"
            type="text"
            className="flex-grow rounded-md border-2 border-gray-400 p-2 text-custom-textColor w-full"
            placeholder={t('example-ai-prompt')}
            value={values.prompt}
            onChange={handleChange}
          />
          <div className="flex flex-col justify-around">
            <button type="submit" className="flex-shrink mx-2">
              <i className="fa-solid fa-paper-plane text-xl" />
            </button>
            <button
              className="flex-shrink mx-2"
              onClick={() => {
                resetForm();
                setResponse(null);
                setError(null);
              }}
            >
              <i className="fa-solid fa-arrow-rotate-right text-xl"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AiPromptChat;
